/**
 * AI:
 * Status: 'confirmed'
 * Type: Component
 * SubType: Presentational
 * Reason: Renders the sticky summary header (record id, status tags, review flags,
 *         parent import id, re-lookup counter) for the EI tooling area.
 */
import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {EiImportRecordDto} from '../../../../../../../model/dto/entity/ei-import-record.dto';
import {EiImportDto} from '../../../../../../../model/dto/entity/ei-import.dto';
import {EiImportRecordStatusInternal} from '../../../../../../../enum/ei-import-record-status-internal.enum';
import {EiImportPhase} from '../../../../../../../enum/ei-import-phase.enum';
import {EiImportExecutionStatus} from '../../../../../../../enum/ei-import-execution-status.enum';

@Component({
    selector: 'ei-tooling-summary-header',
    templateUrl: './ei-tooling-summary-header.component.html',
    styleUrls: ['./ei-tooling-summary-header.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EiToolingSummaryHeaderComponent {

    @Input() record: EiImportRecordDto | null = null;
    @Input() parentImport: EiImportDto | null = null;

    /**
     * Returns a short German label for the record status enum value.
     */
    get recordStatusLabel(): string {
        if (!this.record || this.record.status == null) {
            return 'Unbekannt';
        }
        switch (this.record.status) {
            case EiImportRecordStatusInternal.NEW: return 'Neu';
            case EiImportRecordStatusInternal.RUNNING: return 'Läuft';
            case EiImportRecordStatusInternal.WAITING_FOR_MANUAL_ACTIVITY: return 'Wartet auf Aktion';
            case EiImportRecordStatusInternal.FINISHED: return 'Abgeschlossen';
            case EiImportRecordStatusInternal.FINISHED_NOT_RELEVANT: return 'Nicht relevant';
            case EiImportRecordStatusInternal.FINISHED_WITHOUT_ACTIVITY: return 'Ohne Aktion';
            case EiImportRecordStatusInternal.ERROR: return 'Fehler';
            default: return 'Unbekannt';
        }
    }

    /**
     * Returns a PrimeNG severity keyword for the record status tag.
     */
    get recordStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (!this.record || this.record.status == null) {
            return 'secondary';
        }
        switch (this.record.status) {
            case EiImportRecordStatusInternal.NEW: return 'info';
            case EiImportRecordStatusInternal.RUNNING: return 'info';
            case EiImportRecordStatusInternal.WAITING_FOR_MANUAL_ACTIVITY: return 'warn';
            case EiImportRecordStatusInternal.FINISHED: return 'success';
            case EiImportRecordStatusInternal.FINISHED_NOT_RELEVANT: return 'secondary';
            case EiImportRecordStatusInternal.FINISHED_WITHOUT_ACTIVITY: return 'secondary';
            case EiImportRecordStatusInternal.ERROR: return 'danger';
            default: return 'secondary';
        }
    }

    /**
     * Returns a German label for the import's current phase.
     */
    get phaseLabel(): string {
        if (!this.parentImport || this.parentImport.phase == null) {
            return 'Phase unbekannt';
        }
        switch (this.parentImport.phase) {
            case EiImportPhase.INGESTION: return 'Ingestion';
            case EiImportPhase.OBJECT_RESOLUTION: return 'Objekt-Auflösung';
            case EiImportPhase.OBJECT_REVIEW: return 'Objekt-Prüfung';
            case EiImportPhase.VALIDATION: return 'Validierung';
            case EiImportPhase.PERSISTENCE: return 'Persistenz';
            case EiImportPhase.POST_PROCESSING: return 'Nachbearbeitung';
            case EiImportPhase.DONE: return 'Abgeschlossen';
            default: return 'Phase unbekannt';
        }
    }

    /**
     * Returns a severity for the phase tag, aligned with execution status.
     */
    get phaseSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (!this.parentImport) {
            return 'secondary';
        }
        if (this.parentImport.executionStatus === EiImportExecutionStatus.ERROR) {
            return 'danger';
        }
        if (this.parentImport.phase === EiImportPhase.DONE) {
            return 'success';
        }
        if (this.parentImport.executionStatus === EiImportExecutionStatus.RUNNING) {
            return 'info';
        }
        if (this.parentImport.phase === EiImportPhase.OBJECT_REVIEW) {
            return 'warn';
        }
        return 'secondary';
    }
}
